import { buildProps } from '@element-plus/utils'
import { componentSizes } from '@element-plus/constants'
import type TextEllipsis from './text-ellipsis.vue'
import type { StyleObjectInterface } from './dom'
import type { ExtractPropTypes, PropType } from 'vue'
import type { ElTooltipProps } from '@element-plus/components/tooltip'

export type EllipsisTooltipProps = Partial<
  Pick<
    ElTooltipProps,
    | 'effect'
    | 'placement'
    | 'offset'
    | 'showAfter'
    | 'showArrow'
    | 'enterable'
    | 'hideAfter'
    | 'popperClass'
    | 'popperOptions'
    // | 'transition'
  >
>

export const textEllipsisProps = buildProps({
  /**
   * @description text type
   */
  type: {
    type: String,
    values: ['primary', 'success', 'info', 'warning', 'danger', ''],
    default: '',
  },
  /**
   * @description text size
   */
  size: {
    type: String,
    values: componentSizes,
    default: '',
  },
  /**
   * @description text content
   */
  content: {
    type: String,
  },
  /**
   * @description display rows
   */
  rows: {
    type: Number,
    default: 1,
    validator: (val) => Number.parseInt(val, 10) > 0,
  },
  /**
   * @description display length of chars
   */
  chars: {
    type: Number,
    validator: (val) => Number.parseInt(val, 10) > 0,
  },
  /**
   * @description display height of text content
   */
  height: {
    type: Number,
    validator: (val) => Number.parseInt(val, 10) > 0,
  },
  /**
   * @description show tooltip
   */
  tooltip: {
    type: [Boolean, Object as PropType<EllipsisTooltipProps>],
    default: false,
  },
  /**
   * @description tooltip trigger
   */
  tooltipTrigger: {
    type: String,
    values: ['ellipsis', 'always', 'never'],
    default: 'ellipsis',
  },
  /**
   * @description  text line height
   */
  lineHeight: Number,
  /**
   * @description text break word when wrapping
   */
  breakWord: {
    type: Boolean,
    default: false,
  },
  ellipsisStyle: {
    type: [String, Object as PropType<StyleObjectInterface>],
    default: '',
  },
  /**
   * @description traversal step of text
   */
  step: {
    type: Number,
    default: 4,
    validator: (val) =>
      Number.parseInt(val, 10) > 0 && Number.parseInt(val, 10) <= 20,
  },
  /**
   * @description display arrow to show/hide content
   */
  showArrow: {
    type: Boolean,
    default: false,
  },
  /**
   * @description custom arrow text, default [0] is show text, [1] is hide text
   */
  arrowText: {
    type: [Array, String],
    default: '',
  },
} as const)

export const textEllipsisEmits = ['ellipsis']

export type TextEllipsisProps = ExtractPropTypes<typeof textEllipsisProps>
export type TextEllipsisInstance = InstanceType<typeof TextEllipsis>
